import { CalendarMonthOutlined, InfoOutlined } from '@mui/icons-material';
import { CardContainer } from 'finara-components';

import { usePayableSummary } from '@/hooks/queries/useOrganizationConfig';

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const PayrollPayableCard: React.FC = () => {
  const { data: summary, isLoading, isError } = usePayableSummary();

  return (
    <CardContainer>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-1">
          <CalendarMonthOutlined className="text-brand-blue" fontSize="small" />
          <h3 className="text-lg font-semibold text-brand-white">Próximo Pago de Nómina</h3>
        </div>
        <p className="text-sm text-muted mb-6">
          Monto a pagar en la próxima fecha de pago (neto de adelantos por cobrar).
        </p>

        {isLoading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-5 bg-white/10 rounded w-1/3"></div>
            <div className="h-4 bg-white/10 rounded w-2/3"></div>
          </div>
        ) : isError || !summary ? (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <InfoOutlined className="text-yellow-400 mt-0.5" fontSize="small" />
            <p className="text-sm text-yellow-300">
              No se pudo calcular el próximo pago. Verifica la configuración de nómina y los salarios.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-muted">Fecha de pago</p>
              <p className="text-lg font-semibold text-brand-white">
                {formatDate(summary.payDate)}
              </p>
              <p className="text-xs text-muted">
                Periodo: {formatDate(summary.periodStart)} – {formatDate(summary.periodEnd)}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-muted">Salario bruto</p>
                <p className="text-base font-semibold text-brand-white">
                  {formatCurrency(summary.totalGross)}
                </p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-muted">Descuento adelantos</p>
                <p className="text-base font-semibold text-orange-300">
                  −{formatCurrency(summary.totalDeductions)}
                </p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-muted">Neto a pagar</p>
                <p className="text-base font-semibold text-green-400">
                  {formatCurrency(summary.totalNet)}
                </p>
              </div>
            </div>

            <p className="text-sm text-muted mb-2">
              {summary.employeeCount} empleado(s)
            </p>

            {summary.employees.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted text-left text-xs">
                      <th className="py-2 pr-4 font-medium">Empleado</th>
                      <th className="py-2 px-4 font-medium text-right">Bruto</th>
                      <th className="py-2 px-4 font-medium text-right">Descuento</th>
                      <th className="py-2 pl-4 font-medium text-right">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.employees.map(emp => (
                      <tr key={emp.userId} className="border-t border-border">
                        <td className="py-2 pr-4 text-brand-white">{emp.name}</td>
                        <td className="py-2 px-4 text-right text-brand-white">
                          {formatCurrency(emp.gross)}
                        </td>
                        <td className="py-2 px-4 text-right text-orange-300">
                          {emp.creditDeduction > 0
                            ? `−${formatCurrency(emp.creditDeduction)}`
                            : '—'}
                        </td>
                        <td className="py-2 pl-4 text-right text-green-400">
                          {formatCurrency(emp.net)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </CardContainer>
  );
};
